/**
 * AmbstatNavGroup — «Амбстат» in the CRM sidebar as an expandable group of its seven sections
 * (AV2-4k §3.2), kept outside `Sidebar.jsx` on purpose (§Seam). Expanded: the parent item and a
 * chevron as SIBLINGS in one row, then one sub-item per `AMB_NAV` entry with its badge; open on every
 * entry to /ambstat/*, closed on leaving it, never persisted. Collapsed (56 px): the icon with the
 * Сигналы count in its corner and a click-open flyout portaled to `document.body` (a sidebar child
 * would paint under the head). Every control navigates only when its target differs from the
 * current path + query, so a click on the open section is a history no-op (§3.1).
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { AMB_NAV, useNavBadges } from './nav.js';
import './nav.css';

const fmt = new Intl.NumberFormat('ru-RU').format;

export function AmbstatNavGroup ({ item, collapsed, active }) {
  const navigate = useNavigate();
  const location = useLocation();
  const badges = useNavBadges();
  const [open, setOpen] = useState(active);
  const [wasActive, setWasActive] = useState(active);
  const [flyTop, setFlyTop] = useState(null);
  const iconRef = useRef(null);
  const flyRef = useRef(null);
  if (wasActive !== active) { setWasActive(active); setOpen(active); }

  const Glyph = item.icon;
  const cur = active ? location.pathname.split('/')[2] : null;
  const go = (to) => { if (to !== location.pathname + location.search) navigate(to); };
  const flyOpen = collapsed && flyTop != null;

  useEffect(() => {
    if (!flyOpen) return undefined;
    const box = flyRef.current;
    (box.querySelector('[aria-current="page"]') ?? box.querySelector('button'))?.focus();
    const close = () => { setFlyTop(null); iconRef.current?.focus(); };
    const onDown = (e) => { if (!box.contains(e.target) && !iconRef.current?.contains(e.target)) close(); };
    // Tab past the last item: the flyout is body's last child, so focus would leave the document and onBlur
    // would see no `relatedTarget`; close and return focus to the icon, as Escape does.
    const onKey = (e) => {
      const past = e.key === 'Tab' && !e.shiftKey && e.target === box.querySelector('li:last-child > button');
      if (past) e.preventDefault();
      if (past || e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [flyOpen]);

  const items = AMB_NAV.map(({ id, title }) => (
    <li key={id}>
      <button type="button" className="amb-nav__item" aria-current={id === cur ? 'page' : undefined}
        onClick={() => { setFlyTop(null); go(`/ambstat/${id}`); }}>
        <span className="amb-nav__label">{title}</span>
        {badges[id] ? <span className="amb-nav__badge">{fmt(badges[id])}</span> : null}
      </button>
    </li>
  ));

  if (collapsed) {
    return (
      <>
        <button ref={iconRef} type="button" title={item.label} aria-expanded={flyOpen}
          className={`sidebar__item amb-nav__icon${active ? ' sidebar__item--active' : ''}`}
          onClick={(e) => setFlyTop(flyOpen ? null : e.currentTarget.getBoundingClientRect().top)}>
          <Glyph size={18} strokeWidth={1.6} />
          {badges.signals ? <span className="amb-nav__badge amb-nav__badge--corner">{fmt(badges.signals)}</span> : null}
        </button>
        {flyOpen && createPortal(
          <nav ref={flyRef} className="amb-nav__flyout" aria-label="Разделы Амбстата" style={{ top: flyTop }}
            onBlur={(e) => { if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) setFlyTop(null); }}>
            <ul className="amb-nav__list">{items}</ul>
          </nav>,
          document.body,
        )}
      </>
    );
  }

  const parentCls = open ? (active ? ' amb-nav__parent--on' : '') : (active ? ' sidebar__item--active' : '');
  return (
    <>
      <div className="amb-nav__row">
        <button type="button" className={`sidebar__item${parentCls}`} onClick={() => go(active && cur ? `/ambstat/${cur}` : item.path)}>
          <Glyph size={18} strokeWidth={1.6} />
          <span>{item.label}</span>
        </button>
        <button type="button" className={`amb-nav__chev${open ? ' amb-nav__chev--open' : ''}`} aria-expanded={open}
          aria-controls="amb-nav-list" aria-label="Разделы Амбстата" onClick={() => setOpen((o) => !o)}>
          <ChevronDown size={14} strokeWidth={1.8} />
        </button>
      </div>
      {open && <ul id="amb-nav-list" className="amb-nav__list">{items}</ul>}
    </>
  );
}
